import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import {
  Menu, X, ChevronRight, LayoutDashboard, Users, Megaphone,
  CalendarDays, Trophy, ShieldCheck, LogOut, BellRing, ImageIcon,
  Star, Sliders, ShieldAlert, BarChart3, Palette,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";

const NAV = [
  { to: "/admin",                    label: "Aperçu",          icon: LayoutDashboard, exact: true },
  { to: "/admin/analytics",          label: "Analytics",       icon: BarChart3 },
  { to: "/admin/featured",           label: "Mises en avant",  icon: Star },
  { to: "/admin/cms",                label: "CMS Éditorial",   icon: Palette },
  { to: "/admin/dashboard-control",  label: "Dashboards",      icon: Sliders },
  { to: "/admin/concours-control",   label: "Concours · Pilotage", icon: Trophy },
  { to: "/admin/moderation",         label: "Modération",      icon: ShieldAlert },
  { to: "/admin/users",              label: "Utilisateurs",    icon: Users },
  { to: "/admin/collaborations",     label: "Collaborations",  icon: Megaphone },
  { to: "/admin/events",             label: "Événements",      icon: CalendarDays },
  { to: "/admin/concours",           label: "Concours",        icon: Trophy },
  { to: "/admin/contest-entries",    label: "Participations",  icon: ImageIcon },
  { to: "/admin/broadcast",          label: "Broadcast",       icon: BellRing },
  { to: "/admin/purge",              label: "Nettoyage base",  icon: ShieldAlert },
];

export default function AdminLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();

  const isActive = (to, exact = false) => {
    if (exact) return location.pathname === to;
    if (location.pathname === to) return true;
    return location.pathname.startsWith(to + "/");
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="admin-layout">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex fixed top-0 left-0 h-screen w-64 flex-col bg-gradient-to-b from-[#1A1A1A] to-[#2A1E12] text-white z-30">
        <div className="px-6 pt-7 pb-5 border-b border-white/10">
          <Link to="/admin" className="flex items-center gap-2" data-testid="admin-sidebar-logo">
            <span className="font-serif text-[28px] font-medium tracking-tight text-white">
              Helencia
            </span>
            <span className="text-[9px] tracking-[0.3em] uppercase text-[#E6C29A] font-semibold pt-1">
              Admin
            </span>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.to, item.exact);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-xl text-sm transition-all ${
                  active
                    ? "bg-[#C68E59] text-white font-semibold shadow-sm"
                    : "text-white/75 hover:bg-white/10 hover:text-white"
                }`}
                data-testid={`admin-sidebar-${item.label.toLowerCase().replace(/\s/g, "-").replace("é","e").replace("è","e")}`}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
                {active && <ChevronRight className="w-3.5 h-3.5 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        <div className="px-3 pb-4 border-t border-white/10 pt-4">
          <div className="flex items-center gap-3 px-2 py-2 text-white/85">
            <div className="w-9 h-9 rounded-full bg-[#C68E59] flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-[10px] uppercase tracking-widest text-[#E6C29A]">Administrateur</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-xs rounded-xl text-white/70 hover:bg-white/10 hover:text-white transition"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Top bar mobile */}
      <header className="lg:hidden sticky top-0 z-40 bg-[#1A1A1A] text-white border-b border-white/10">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/admin" className="font-serif text-xl">Helencia · Admin</Link>
          <button onClick={() => setMobileOpen(true)} className="p-1">
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setMobileOpen(false)} />
          <aside className="fixed top-0 left-0 h-screen w-72 bg-gradient-to-b from-[#1A1A1A] to-[#2A1E12] text-white z-50 lg:hidden shadow-2xl">
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-white/10">
              <span className="font-serif text-2xl text-white">Admin</span>
              <button onClick={() => setMobileOpen(false)} className="p-1 text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="p-3">
              {NAV.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.to, item.exact);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 my-0.5 rounded-xl text-sm ${
                      active ? "bg-[#C68E59] text-white font-semibold" : "text-white/85"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </aside>
        </>
      )}

      {/* Main content */}
      <main className="lg:pl-64 min-h-screen">
        {/* Desktop topbar */}
        <div className="hidden lg:flex sticky top-0 z-20 bg-[#FFFBF5]/90 backdrop-blur border-b border-[#EADDC9] px-10 h-14 items-center justify-end">
          <NotificationBell listHref="/admin" testId="admin-top-notifications-bell" />
        </div>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10 py-10">
          {children}
        </div>
      </main>
    </div>
  );
}
